import {
  Check,
  ContextualMenuItemType,
  IBreadcrumbItem,
  type IColumn,
  Icon,
  IContextualMenuItem,
  mergeStyles,
  MessageBarType,
} from '@fluentui/react';
import {
  Button,
  Checkbox,
  ClassNamesFromIStyles,
  defaultPalette,
  Item,
  itemStylesTag,
  TextWithCopy,
  TooltipHost,
  useClassNames,
  useHaicPageTitle,
  useToast,
} from '@h2oai/ui-kit';
import { formatBaseUnitCpu, formatBaseUnitMemory, resourceStringToBaseUnit } from 'kubernetes-resource-units';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useUsers } from '../../authz/providers/UsersProvider';
import { FailedToLoadView } from '../../components/FailedToLoadView/FailedToLoadView';
import { HeaderPure } from '../../components/Header/Header';
import { NoItemView } from '../../components/NoItemView/NoItemView';
import { RowHeaderTitle } from '../../components/RowHeaderTitle/RowHeaderTitle';
import { hideTextWithCopyTextStyles, UuidTruncated } from '../../components/UuidTruncated/UuidTruncated';
import WidgetList from '../../components/WidgetList/WidgetList';
import { DeploymentFixed, DeploymentMode, getDeploymentMode } from '../../mlops/api';
import { deploymentName } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_pb';
import { KubernetesResourceSpec } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/kubernetes_resource_spec_pb';
import {
  useMutationBatchDeleteDeployments,
  useMutationDeleteDeployment,
  useQueryDeployments,
} from '../../mlops/mlops.deployer.hooks';
import { useQueryListProjects } from '../../mlops/mlops.storage.hooks';
import { useInfiniteQueryFetchAllPages } from '../../services/hooks';
import { GrpcStatusCode, isGrpcGatewayErrorResponse } from '../../services/HTTPResponse';
import { classNameButtonReset } from '../../themes/common';
import { SelectedContext, useSelected } from '../../utils/contexts';
import { useDebouncedCallback, useEnv } from '../../utils/hooks';
import { NONE_OPTION_KEY, noneOption } from '../../utils/render';
import { type SortProps } from '../../utils/sortUtils';
import { formatError } from '../../utils/utils';
import { ContextMenuIconButton } from '../Orchestrator/Workflows';
import { useConfirmDialog } from './ConfirmDialogProvider';
import { WORKSPACE_DEPLOYMENT_ID_SEPARATOR } from './constants';
import { ListItemDescription } from './Models';
import { NavigationWrapperNoMagic } from './NavigationWrapperNoMagic';
import PageWrapper from './PageWrapper';
import { MLOpsUserActionType } from './userActions';

type DeploymentEnhanced = DeploymentFixed & {
  creatorDisplayName?: string;
  id: string;
  mode: DeploymentMode;
  modeType: DeploymentMode['type'];
  monitoringEnabled: boolean;
  projectDisplayName?: string;
  projectId: string;
  resourceSpecs?: KubernetesResourceSpec[];
  totalLimitCpu: number;
  totalLimitMem: number;
  totalReplicaCount: number;
  totalRequestCpu: number;
  totalRequestMem: number;
  securityEnabled: boolean;
};

type KubernetesResourceData = Pick<
  DeploymentEnhanced,
  `resourceSpecs` | `totalLimitCpu` | `totalLimitMem` | `totalReplicaCount` | `totalRequestCpu` | `totalRequestMem`
>;

const getResourceSpecs = (mode: DeploymentMode): KubernetesResourceSpec[] => {
  switch (mode.type) {
    case `single`:
      return [mode.params.kubernetesResourceSpec].filter(Boolean) as KubernetesResourceSpec[];
    case `shadow`:
      return [
        mode.params.primaryElement.kubernetesResourceSpec,
        mode.params.secondaryElement.kubernetesResourceSpec,
      ].filter(Boolean) as KubernetesResourceSpec[];
    case `split`:
      return mode.params.splitElements
        .map((se) => se.kubernetesResourceSpec)
        .filter(Boolean) as KubernetesResourceSpec[];
    default:
      // @ts-expect-error exhaustive switch check `mode.type` should be of type `never` here
      throw new Error(`Unsupported deployment mode type: ${mode.type}`);
  }
};

const getKubernetesResourceData = (mode: DeploymentMode): KubernetesResourceData => {
  const resourceSpecs = getResourceSpecs(mode);
  let totalReplicaCount = 0;
  let totalLimitCpu = 0;
  let totalLimitMem = 0;
  let totalRequestCpu = 0;
  let totalRequestMem = 0;
  for (const { kubernetesResourceRequirements, replicas } of resourceSpecs) {
    totalReplicaCount += replicas || 1; // Default to 1 if replicas is not set
    if (kubernetesResourceRequirements) {
      const { limits, requests } = kubernetesResourceRequirements;
      if (limits) {
        if (limits.cpu) {
          totalLimitCpu += Number(resourceStringToBaseUnit(limits.cpu));
        }
        if (limits.memory) {
          totalLimitMem += Number(resourceStringToBaseUnit(limits.memory));
        }
      }
      if (requests) {
        if (requests.cpu) {
          totalRequestCpu += Number(resourceStringToBaseUnit(requests.cpu));
        }
        if (requests.memory) {
          totalRequestMem += Number(resourceStringToBaseUnit(requests.memory));
        }
      }
    }
  }
  return { totalLimitCpu, totalLimitMem, totalReplicaCount, totalRequestCpu, totalRequestMem, resourceSpecs };
};

export const enhanceDeployment = (deployment: DeploymentFixed): DeploymentEnhanced => {
  const { deployment: deploymentId, workspace: projectId } = deploymentName.parse(deployment.name!);
  const mode = getDeploymentMode(deployment);
  const deploymentEnhanced: DeploymentEnhanced = {
    ...deployment,
    ...getKubernetesResourceData(mode),
    id: deploymentId,
    mode,
    modeType: mode.type,
    monitoringEnabled: Boolean(deployment.monitoringOptions?.enabled),
    projectId,
    securityEnabled: Boolean(deployment.security && !(`disabledSecurity` in deployment.security)),
  };
  return deploymentEnhanced;
};

const modeToItemData = (
  mode: DeploymentMode
): { name: React.ReactNode; style: { backgroundColor: string; color: string } } => {
  switch (mode.type) {
    case `single`:
      return { name: `Single`, style: { backgroundColor: defaultPalette.blue100, color: defaultPalette.blue900 } };
    case `shadow`:
      return { name: `Shadow`, style: { backgroundColor: defaultPalette.gray200, color: defaultPalette.gray900 } };
    case `split`:
      return { name: `Split`, style: { backgroundColor: defaultPalette.green100, color: defaultPalette.green900 } };
  }
};

const classNameInfoTooltip = mergeStyles({
  marginInlineStart: `1ch`,
});

const classNameSmallColumnTitle = mergeStyles({
  '& .ms-DetailsHeader-cellName': {
    fontSize: `0.8em`,
  },
});

const K8sResourceMoreInfoTooltip = ({ tooltipContent }: { tooltipContent: React.ReactElement }) => {
  return (
    <TooltipHost
      hostClassName={classNameInfoTooltip}
      content={tooltipContent}
      styles={{
        root: { verticalAlign: 'middle' },
      }}
    >
      <Icon iconName="Info" style={{ color: 'var(--h2o-gray900)', fontSize: '14px' }} />
    </TooltipHost>
  );
};

type DeploymentsOrderByKeys = 'create_time' | 'display_name' | 'update_time';

const deploymentOrderKeyToText: Record<DeploymentsOrderByKeys, string> = {
  create_time: `Creation Time`,
  display_name: `Display Name`,
  update_time: `Update Time`,
};

type DeploymentsFilterState =
  | undefined
  | {
      search?: string;
    };

const useFilterAndOrderBy = (): {
  filter: DeploymentsFilterState;
  setFilter: (
    newStateOrUpdater: DeploymentsFilterState | ((prev: DeploymentsFilterState) => DeploymentsFilterState)
  ) => void;
  setSort: (newStateOrUpdater: SortProps | ((prev: SortProps) => SortProps)) => void;
  sort: SortProps;
} => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const searchParamsRef = React.useRef(searchParams);
  searchParamsRef.current = searchParams;

  const filter = React.useMemo<DeploymentsFilterState>(() => {
    const fromSearchParams: DeploymentsFilterState = {
      search: searchParams.get(`search`) || undefined,
    };
    if (Object.values(fromSearchParams).some(Boolean)) {
      return fromSearchParams;
    }
    return undefined;
  }, [searchParams]);
  const filterRef = React.useRef(filter);
  filterRef.current = filter;
  const setFilter = (
    newStateOrUpdater: DeploymentsFilterState | ((prev: DeploymentsFilterState) => DeploymentsFilterState)
  ) => {
    const newFilter =
      typeof newStateOrUpdater === `function` ? newStateOrUpdater(filterRef.current) : newStateOrUpdater;
    const sp = searchParamsRef.current;
    if (newFilter?.search) {
      sp.set(`search`, newFilter.search);
    } else {
      sp.delete(`search`);
    }

    const newSearch = sp.toString();
    if (newSearch !== window.location.search.slice(1)) {
      history.replace({ ...location, search: newSearch });
    }
  };

  const sort: SortProps = React.useMemo(() => {
    const orderKey = searchParams.get(`orderBy`);
    const descending = searchParams.get(`desc`) === `1`;
    return {
      key: (orderKey as DeploymentsOrderByKeys) || undefined,
      descending: descending,
    };
  }, [searchParams]);
  const sortRef = React.useRef(sort);
  sortRef.current = sort;
  const setSort = (newStateOrUpdater: SortProps | ((prev: SortProps) => SortProps)) => {
    const newOrderBy = typeof newStateOrUpdater === `function` ? newStateOrUpdater(sortRef.current) : newStateOrUpdater;
    const sp = searchParamsRef.current;
    if (newOrderBy.key) {
      sp.set(`orderBy`, newOrderBy.key);
    } else {
      sp.delete(`orderBy`);
    }
    if (newOrderBy.descending) {
      sp.set(`desc`, `1`);
    } else {
      sp.delete(`desc`);
    }
    const newSearch = sp.toString();
    if (newSearch !== window.location.search.slice(1)) {
      history.replace({ ...location, search: newSearch });
    }
  };
  return { filter, sort, setFilter, setSort };
};

type DeploymentsOrderByProps = {
  className?: string;
  descending: boolean;
  onChange: (key: DeploymentsOrderByKeys | undefined, descending: boolean) => void;
  orderKey: DeploymentsOrderByKeys | undefined;
};

const DeploymentsOrderBy = (props: DeploymentsOrderByProps) => {
  const { descending, onChange, orderKey } = props;

  const sortByMenuItems: IContextualMenuItem[] = Object.entries(deploymentOrderKeyToText).map(([key, text]) => ({
    key,
    text,
  }));

  const orderMenuItems: IContextualMenuItem[] = [
    {
      key: `ascending`,
      text: `Ascending`,
    },
    {
      key: `descending`,
      text: `Descending`,
    },
  ];

  const onMenuItemClick = (_ev: any, menuItems: IContextualMenuItem | undefined) => {
    if (!menuItems) return;
    const key = menuItems.key;
    if (key === NONE_OPTION_KEY) {
      onChange(undefined, false);
      return;
    }
    if (key === 'ascending' || key === 'descending') {
      onChange(orderKey, key === 'descending');
      return;
    }
    if (deploymentOrderKeyToText[key as DeploymentsOrderByKeys]) {
      onChange(key as DeploymentsOrderByKeys, descending);
      return;
    }
  };

  const menuItems = React.useMemo<IContextualMenuItem[]>(() => {
    const sortByMenuItemsWithActive = sortByMenuItems.map((item) => {
      const isActive = item.key === orderKey;
      return {
        ...item,
        onClick: onMenuItemClick,
        iconProps: isActive ? { iconName: 'CheckMark' } : undefined,
      };
    });
    const orderMenuItemsWithActive = orderMenuItems.map((item) => {
      const isActive = item.key === (descending ? 'descending' : 'ascending');
      return {
        ...item,
        disabled: !orderKey,
        onClick: onMenuItemClick,
        iconProps: isActive ? { iconName: 'CheckMark' } : undefined,
      };
    });

    return [
      {
        key: 'sortBy',
        itemType: ContextualMenuItemType.Section,
        sectionProps: {
          topDivider: true,
          bottomDivider: true,
          title: 'Sort By',
          items: [
            {
              ...noneOption,
              onClick: onMenuItemClick,
            },
            ...sortByMenuItemsWithActive,
          ],
        },
      },
      {
        key: `order`,
        itemType: ContextualMenuItemType.Section,
        sectionProps: {
          title: `Order`,
          items: orderMenuItemsWithActive,
        },
      },
    ];
  }, [descending, orderKey]);

  return (
    <Button
      className={props.className}
      menuIconProps={{ iconName: !orderKey ? `Sort` : descending ? `Descending` : `Ascending` }}
      menuItems={menuItems}
      text={(orderKey && deploymentOrderKeyToText[orderKey]) || `Sort By`}
    />
  );
};

const useColumns = ({
  onDeleteDeployment,
  onView,
  onViewDeploymentInWorkspace,
  sortProps,
  selectedDeployments,
}: {
  onDeleteDeployment: (item: DeploymentEnhanced) => void;
  onView: (item: DeploymentEnhanced) => void;
  onViewDeploymentInWorkspace: (item: DeploymentEnhanced) => void;
  sortProps: SortProps;
  selectedDeployments: SelectedContext;
}) => {
  const selectionColumnWidth = 60;
  const selectionListCellStyles: Partial<any> = {
    root: {
      display: `flex`,
      justifyContent: `center`,
    },
  };

  const toggleSelection = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
      event.stopPropagation();
      selectedDeployments.toggleSelected(id);
    },
    [selectedDeployments]
  );

  return React.useMemo<IColumn[]>(
    () => [
      {
        key: `selection`,
        name: ``,
        minWidth: selectionColumnWidth,
        maxWidth: selectionColumnWidth,
        data: {
          listCellProps: {
            emptyMessage: '',
            onRenderText: (item: DeploymentEnhanced) => {
              const isSelected = selectedDeployments.isSelected(item.name!);
              return (
                <button
                  className={classNameButtonReset}
                  onClick={(event) => toggleSelection(event, item.name!)}
                  type="button"
                >
                  <Check checked={isSelected} />
                </button>
              );
            },
            styles: selectionListCellStyles,
          },
        },
      },
      {
        key: 'displayName',
        name: 'Deployment Name',
        fieldName: 'creatorDisplayName',
        minWidth: 150,
        maxWidth: 300,
        flexGrow: 1,
        data: {
          headerFieldName: 'displayName',
          listCellProps: {
            onRenderHeader: (item: DeploymentEnhanced) =>
              RowHeaderTitle({
                title: item.displayName,
                onClick: () => onView(item),
              }),
            onRenderText: ({ creatorDisplayName, createTime }: DeploymentEnhanced) => {
              return <ListItemDescription createdBy={creatorDisplayName || ''} createdAt={createTime || ''} />;
            },
          },
        },
      },
      {
        key: `id`,
        name: `Deployment ID`,
        fieldName: `id`,
        minWidth: 160,
        maxWidth: 170,
        data: {
          listCellProps: {
            onRenderText: (item: DeploymentEnhanced) => {
              return <UuidTruncated uuid={item.id} />;
            },
          },
        },
      },
      {
        key: `modeType`,
        name: `Mode`,
        fieldName: `modeType`,
        minWidth: 70,
        maxWidth: 90,
        data: {
          listCellProps: {
            onRenderText: (item: DeploymentEnhanced) => {
              const itemData = modeToItemData(item.mode);
              return <Item data={itemData} styleField="style" styles={itemStylesTag} />;
            },
          },
        },
      },
      {
        key: `projectDisplayName`,
        name: `Workspace`,
        fieldName: `projectDisplayName`,
        minWidth: 120,
        maxWidth: 250,
        data: {
          listCellProps: {
            onRenderText: ({ projectDisplayName, projectId }: DeploymentEnhanced) => {
              return (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <div
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      alignSelf: 'center',
                      marginTop: 4,
                    }}
                  >
                    {projectDisplayName}
                  </div>
                  <TextWithCopy id={projectId} text={projectId} styles={hideTextWithCopyTextStyles} />
                </div>
              );
            },
          },
        },
      },
      {
        key: `monitoringEnabled`,
        name: `Monitoring`,
        fieldName: `monitoringEnabled`,
        minWidth: 60,
        maxWidth: 100,
        headerClassName: classNameSmallColumnTitle,
        data: {
          listCellProps: {
            onRenderText: (item: DeploymentEnhanced) => {
              return item.monitoringEnabled ? (
                <TooltipHost content="Monitoring Enabled">✅</TooltipHost>
              ) : (
                <TooltipHost content="Monitoring Disabled">❌</TooltipHost>
              );
            },
          },
        },
      },
      {
        key: `securityEnabled`,
        name: `Security`,
        fieldName: `securityEnabled`,
        minWidth: 60,
        maxWidth: 100,
        headerClassName: classNameSmallColumnTitle,
        data: {
          listCellProps: {
            onRenderText: (item: DeploymentEnhanced) => {
              return item.securityEnabled ? (
                <TooltipHost content="Security Enabled">✅</TooltipHost>
              ) : (
                <TooltipHost content="Security Disabled">❌</TooltipHost>
              );
            },
          },
        },
      },
      {
        key: `totalReplicaCount`,
        name: `Replicas`,
        fieldName: `totalReplicaCount`,
        minWidth: 70,
        maxWidth: 90,
        data: {
          listCellProps: {
            onRenderText: (item: DeploymentEnhanced) => {
              const { resourceSpecs } = item;
              const tooltip: React.ReactElement | undefined =
                (resourceSpecs?.length || 0) <= 1 ? undefined : (
                  <K8sResourceMoreInfoTooltip
                    tooltipContent={
                      <>
                        Multiple machines
                        <br />
                        {resourceSpecs!.map((rs) => String(rs.replicas || 0)).join(`\xA0+\xA0`)}
                      </>
                    }
                  />
                );
              return (
                <>
                  {item.totalReplicaCount}
                  {tooltip}
                </>
              );
            },
          },
        },
      },
      {
        key: `totalRequestCpu`,
        name: `CPU Request`,
        fieldName: `totalRequestCpu`,
        minWidth: 90,
        maxWidth: 100,
        data: {
          listCellProps: {
            onRenderText: (item: DeploymentEnhanced) => {
              if (item.totalRequestCpu === 0) return <>&minus;</>;
              const { resourceSpecs } = item;
              const tooltip: React.ReactElement | undefined =
                (resourceSpecs?.length || 0) <= 1 ? undefined : (
                  <K8sResourceMoreInfoTooltip
                    tooltipContent={
                      <>
                        Multiple machines
                        <br />
                        {resourceSpecs!
                          .map((rs) => String(rs.kubernetesResourceRequirements?.requests?.cpu || 0))
                          .join(`\xA0+\xA0`)}
                      </>
                    }
                  />
                );
              return (
                <>
                  {formatBaseUnitCpu(item.totalRequestCpu, { maximumFractionDigits: 3 })}
                  {tooltip}
                </>
              );
            },
          },
        },
      },
      {
        key: `totalLimitCpu`,
        name: `CPU Limit`,
        fieldName: `totalLimitCpu`,
        minWidth: 90,
        maxWidth: 100,
        data: {
          listCellProps: {
            onRenderText: (item: DeploymentEnhanced) => {
              if (item.totalLimitCpu === 0) return <>&minus;</>;
              const { resourceSpecs } = item;
              const tooltip: React.ReactElement | undefined =
                (resourceSpecs?.length || 0) <= 1 ? undefined : (
                  <K8sResourceMoreInfoTooltip
                    tooltipContent={
                      <>
                        Multiple machines
                        <br />
                        {resourceSpecs!
                          .map((rs) => String(rs.kubernetesResourceRequirements?.limits?.cpu || 0))
                          .join(`\xA0+\xA0`)}
                      </>
                    }
                  />
                );
              return (
                <>
                  {formatBaseUnitCpu(item.totalLimitCpu, { maximumFractionDigits: 3 })}
                  {tooltip}
                </>
              );
            },
          },
        },
      },
      {
        key: `totalRequestMem`,
        name: `MEM Request`,
        fieldName: `totalRequestMem`,
        minWidth: 80,
        maxWidth: 100,
        data: {
          listCellProps: {
            onRenderText: (item: DeploymentEnhanced) => {
              if (item.totalRequestMem === 0) return <>&minus;</>;
              const { resourceSpecs } = item;
              const tooltip: React.ReactElement | undefined =
                (resourceSpecs?.length || 0) <= 1 ? undefined : (
                  <K8sResourceMoreInfoTooltip
                    tooltipContent={
                      <>
                        Multiple machines
                        <br />
                        {resourceSpecs!
                          .map((rs) => String(rs.kubernetesResourceRequirements?.requests?.memory || 0))
                          .join(`\xA0+\xA0`)}
                      </>
                    }
                  />
                );
              return (
                <>
                  {formatBaseUnitMemory(item.totalRequestMem, { maximumFractionDigits: 3 })}
                  {tooltip}
                </>
              );
            },
          },
        },
      },
      {
        key: `totalLimitMem`,
        name: `MEM Limit`,
        fieldName: `totalLimitMem`,
        minWidth: 80,
        maxWidth: 100,
        data: {
          listCellProps: {
            onRenderText: (item: DeploymentEnhanced) => {
              if (item.totalLimitMem === 0) return <>&minus;</>;
              const { resourceSpecs } = item;
              const tooltip: React.ReactElement | undefined =
                (resourceSpecs?.length || 0) <= 1 ? undefined : (
                  <K8sResourceMoreInfoTooltip
                    tooltipContent={
                      <>
                        Multiple machines
                        <br />
                        {resourceSpecs!
                          .map((rs) => String(rs.kubernetesResourceRequirements?.limits?.memory || 0))
                          .join(`\xA0+\xA0`)}
                      </>
                    }
                  />
                );
              return (
                <>
                  {formatBaseUnitMemory(item.totalLimitMem, { maximumFractionDigits: 3 })}
                  {tooltip}
                </>
              );
            },
          },
        },
      },
      {
        key: `actions`,
        name: '',
        minWidth: 100,
        data: {
          listCellProps: {
            emptyMessage: '',
            onRenderText: (item: DeploymentEnhanced) => {
              return (
                <ContextMenuIconButton
                  items={[
                    {
                      key: `view`,
                      text: 'View',
                      onClick: () => onView(item),
                      iconProps: { iconName: 'Info', style: { color: 'var(--h2o-gray900)' } },
                    },
                    {
                      key: 'view-in-workspace',
                      text: 'View in workspace',
                      onClick: () => onViewDeploymentInWorkspace(item),
                      iconProps: { iconName: 'OpenInNewWindow', style: { color: 'var(--h2o-gray900)' } },
                    },
                    {
                      key: 'deleteDeployment',
                      text: 'Delete deployment',
                      // TODO: Handle disabled state when deployment status is present in deployment admin view.
                      // disabled: !item.status.updatesAllowed,
                      onClick: () => onDeleteDeployment(item),
                      style: { color: 'var(--h2o-red400)' },
                      iconProps: { iconName: 'Delete', style: { color: 'var(--h2o-red400)' } },
                    },
                  ]}
                />
              );
            },
            styles: {
              root: {
                display: 'flex',
                flexGrow: 1,
                justifyContent: 'end',
                marginRight: 9,
              },
            },
          },
        },
      },
    ],
    [onDeleteDeployment, onView, sortProps, selectedDeployments]
  );
};

const styles = {
  buttonSortBy: {
    marginInlineStart: `auto`,
  },
  selectionPanel: {
    alignItems: `center`,
    display: `flex`,
    flexWrap: `wrap`,
    gap: `1rem`,
    minBlockSize: `2rem`,

    '& > p': {
      margin: 0,
    },
  },
};

const SelectionPanel = (props: {
  className: string;
  deployments: DeploymentEnhanced[] | undefined;
  onBatchDelete: (deploymentNames: string[]) => void;
  selectedDeployments: SelectedContext;
}) => {
  const { deployments, selectedDeployments } = props;
  const deploymentNames = deployments?.map((d) => d.name!) || [];
  const deploymentsCount = deploymentNames.length;

  const selectedDeploymentNames = React.useMemo<Set<string>>(() => {
    // If defaultSelected is true, selected items are all deployment names EXCEPT those in deviatesFromDefault
    if (selectedDeployments.defaultSelected) {
      const allNames = new Set(deploymentNames);
      // @ts-expect-error new Set methods not available in TypeScript < 5.5
      return allNames.difference(selectedDeployments.deviatesFromDefault);
    }
    // If defaultSelected is false, selected items are ONLY those in deviatesFromDefault
    return selectedDeployments.deviatesFromDefault;
  }, [selectedDeployments, deploymentNames]);

  const selectedCount = selectedDeploymentNames.size;
  const anySelected = selectedCount > 0;
  const allSelected = deploymentsCount > 0 && selectedCount === deploymentsCount;

  const onSelectAllChange = React.useCallback(() => {
    if (allSelected) {
      selectedDeployments.emptySelection();
    } else {
      // Select all visible deployments.
      selectedDeployments.setSelectedItems(deploymentNames, true);
    }
  }, [allSelected, selectedDeployments, deploymentNames]);

  return (
    <div className={props.className}>
      <Checkbox
        checked={allSelected}
        label="Select all"
        boxSide="end"
        onChange={onSelectAllChange}
        disabled={deploymentsCount === 0}
      />
      {anySelected && (
        <>
          <p>
            Selected{' '}
            <span>
              {selectedCount} deployment{selectedCount > 1 ? `s` : ``}
            </span>
          </p>
          <Button
            styles={{ root: { backgroundColor: 'var(--h2o-red400)', color: 'white' } }}
            onClick={() => {
              props.onBatchDelete(Array.from(selectedDeploymentNames));
            }}
          >
            Delete Selected
          </Button>
        </>
      )}
    </div>
  );
};

export type AdminDeploymentsPureProps = {
  deployments: DeploymentEnhanced[] | undefined; // passing undefined means loading
  errorMessage?: React.ReactNode; // if set, displays full-page error message
  onDeleteDeployment: (deployment: DeploymentEnhanced) => void;
  onGoToHome: () => void;
  onViewDeployment: (deployment: DeploymentEnhanced) => void;
  onViewDeploymentInWorkspace: (deployment: DeploymentEnhanced) => void;
  onBatchDelete: (deploymentNames: string[]) => void;
  filter?: DeploymentsFilterState;
  setFilter: (
    newStateOrUpdater: DeploymentsFilterState | ((prev: DeploymentsFilterState) => DeploymentsFilterState)
  ) => void;
  setSort: (props: SortProps) => void;
  sort: SortProps;
  searchText: string;
  setSearchText: (text: string) => void;
  isLoadingSearch: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
};

export const AdminDeploymentsPure = (props: AdminDeploymentsPureProps) => {
  const {
    deployments,
    errorMessage,
    filter,
    setFilter,
    setSort,
    sort,
    searchText,
    setSearchText,
    isLoadingSearch,
    onBatchDelete,
    onLoadMore,
    isLoadingMore,
  } = props;

  const breadcrumbs: IBreadcrumbItem[] = [
    {
      key: `home`,
      text: `home`,
      onClick: props.onGoToHome,
    },
    {
      key: `admin-deployments`,
      text: `Admin Deployments`,
      isCurrentItem: true,
    },
  ];
  const classNames = useClassNames<typeof styles, ClassNamesFromIStyles<typeof styles>>(`AdminDeployments`, styles);
  const selectedDeployments = useSelected();

  const setSortAndEmptySelection = (newSortKey: DeploymentsOrderByKeys | undefined, newDescending: boolean): void => {
    setSort({ key: newSortKey, descending: newDescending });
    selectedDeployments.emptySelection();
  };

  const columns = useColumns({
    onView: props.onViewDeployment,
    onViewDeploymentInWorkspace: props.onViewDeploymentInWorkspace,
    onDeleteDeployment: props.onDeleteDeployment,
    sortProps: sort,
    selectedDeployments,
  });

  const handleSearchChange = (newSearch: string) => {
    setSearchText(newSearch);
    if (!newSearch) {
      setFilter(undefined);
    } else {
      setFilter((prev) => ({ ...prev, search: newSearch.trim() }));
    }
    selectedDeployments.emptySelection();
  };

  const debouncedHandleSearch = useDebouncedCallback(handleSearchChange, 500);

  return (
    <PageWrapper>
      <HeaderPure items={breadcrumbs} pageTitle="MLOps Deployments" />
      {errorMessage ? (
        <FailedToLoadView title="Failed to load deployments" description={errorMessage} />
      ) : deployments?.length === 0 && !filter ? (
        <NoItemView title="No deployments" description="Your cloud environment has no deployments." />
      ) : (
        <WidgetList
          items={deployments}
          columns={columns}
          loading={!deployments}
          isLoadingSearch={isLoadingSearch}
          isLoadingMore={isLoadingMore}
          onLoadMore={onLoadMore}
          searchProps={{
            placeholder: 'Search deployments by name',
            onSearchChange: (value) => {
              setSearchText(value);
              debouncedHandleSearch(value);
            },
            searchText: searchText,
          }}
          customActions={
            <>
              <DeploymentsOrderBy
                className={classNames.buttonSortBy}
                descending={sort.descending}
                onChange={setSortAndEmptySelection}
                orderKey={sort.key as DeploymentsOrderByKeys}
              />
            </>
          }
          topPanelSlot={
            <SelectionPanel
              className={classNames.selectionPanel}
              deployments={deployments}
              onBatchDelete={onBatchDelete}
              selectedDeployments={selectedDeployments}
            />
          }
          NoItemsContent={
            filter ? (
              <NoItemView
                title="No deployments matching active filters"
                description="Try a different search term or clear all to see all deployments."
              />
            ) : (
              <NoItemView title="No deployments" description="Your cloud environment has no deployments." />
            )
          }
        />
      )}
    </PageWrapper>
  );
};

const AdminDeploymentsConnected = () => {
  const history = useHistory();
  const env = useEnv();
  const { filter, setFilter, setSort, sort } = useFilterAndOrderBy();
  const [searchText, setSearchText] = React.useState(filter?.search || ``);

  const handleViewDeployment = React.useCallback(
    (deployment: DeploymentEnhanced) => {
      const { deployment: deploymentId, workspace: workspaceId } = deploymentName.parse(deployment.name!);
      history.push(`/mlops/admin/deployments/${workspaceId}${WORKSPACE_DEPLOYMENT_ID_SEPARATOR}${deploymentId}`);
    },
    [history]
  );

  const handleViewDeploymentInWorkspace = React.useCallback(
    (deployment: DeploymentEnhanced) => {
      const { deployment: deploymentId, workspace: workspaceId } = deploymentName.parse(deployment.name!);
      window.open(`/mlops/workspaces/${workspaceId}/deployments/${deploymentId}`, '_blank');
    },
    [history]
  );

  const { addToast } = useToast();
  const { showDialog } = useConfirmDialog();

  const batchDeleteDeploymentMutation = useMutationBatchDeleteDeployments({
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to delete selected deployments: ${formatError(err)}`,
        timeout: 20_000,
      });
    },
    onSuccess: (deletedCount) => {
      addToast({
        messageBarType: MessageBarType.success,
        message: `${deletedCount} deployment${deletedCount > 1 ? 's' : ''} deleted successfully.`,
      });
    },
  });

  const handleBatchDelete = React.useCallback(
    (deploymentNames: string[]) => {
      showDialog?.({
        title: 'Batch delete deployments?',
        description: `This will delete ${deploymentNames.length} deployment${
          deploymentNames.length > 1 ? 's' : ''
        }. This action cannot be undone.`,
        onConfirm: () => {
          batchDeleteDeploymentMutation.mutate(deploymentNames);
        },
      });
    },
    [showDialog, batchDeleteDeploymentMutation]
  );

  const deleteDeploymentMutation = useMutationDeleteDeployment({
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to delete deployment: ${formatError(err)}`,
      });
    },
    onSuccess() {
      addToast({
        messageBarType: MessageBarType.success,
        message: `Deployment deleted successfully`,
      });
    },
  });

  const handleDeleteDeployment = React.useCallback(
    (deployment: DeploymentEnhanced) => {
      showDialog?.({
        title: 'Delete deployment?',
        description: `Are you sure you want to delete deployment "${deployment.displayName}"? This action cannot be undone.`,
        onConfirm: () => {
          deleteDeploymentMutation.mutate({ name: deployment.name! });
        },
      });
    },
    [showDialog, deleteDeploymentMutation]
  );

  const deploymentsQuery = useQueryDeployments(
    {
      workspaceId: `-`,
      orderBy: sort.key ? `${sort.key} ${sort.descending ? 'desc' : 'asc'}` : undefined,
      filter: filter?.search ? `display_name = '*${filter.search}*'` : undefined,
      pageSize: 20,
    },
    {
      meta: {
        toastError: 'Failed to load deployments',
      },
      retry(failureCount, error) {
        const insufficientPermissions =
          isGrpcGatewayErrorResponse(error) && error.code === GrpcStatusCode.PERMISSION_DENIED;
        // Skip retrying if the user does not have permission to view deployments, otherwise retry up to 3 times
        return !insufficientPermissions && failureCount <= 3;
      },
      select: (data) => {
        const flattenedEnhancedDeployments = data.pages.flatMap((page) => {
          if (!page.deployments) return [];
          return page.deployments.map(enhanceDeployment);
        });
        return { pages: flattenedEnhancedDeployments, pageParams: data.pageParams };
      },
    }
  );

  const projectsQuery = useQueryListProjects(
    {},
    {
      select: (data) => {
        const flattenedProjects = data.pages.flatMap((page) => page.project || []);
        const projectsByIdEntries = flattenedProjects.map((p) => [p.id, p]);
        return Object.fromEntries(projectsByIdEntries);
      },
    }
  );
  useInfiniteQueryFetchAllPages(projectsQuery);
  const projectsById = projectsQuery.data;

  const { usersNameMap } = useUsers();

  const deploymentsWithExtraData = React.useMemo<DeploymentEnhanced[] | undefined>(() => {
    return deploymentsQuery.data?.pages.map((d) => {
      return {
        ...d,
        creatorDisplayName: usersNameMap?.[d.creator!]?.displayName,
        projectDisplayName: projectsById?.[d.projectId]?.displayName || ``,
      };
    });
  }, [deploymentsQuery.data?.pages, projectsById, usersNameMap]);

  useHaicPageTitle('Deployments | MLOps Admin', env?.cloudInstanceName);

  const isLoadingSearch = deploymentsQuery.isFetching && !!filter?.search;

  return (
    <AdminDeploymentsPure
      deployments={deploymentsWithExtraData}
      errorMessage={!deploymentsQuery.error ? undefined : formatError(deploymentsQuery.error)}
      onGoToHome={() => history.push('/')}
      onViewDeployment={handleViewDeployment}
      onViewDeploymentInWorkspace={handleViewDeploymentInWorkspace}
      onDeleteDeployment={handleDeleteDeployment}
      onBatchDelete={handleBatchDelete}
      filter={filter}
      setFilter={setFilter}
      setSort={setSort}
      sort={sort}
      searchText={searchText}
      setSearchText={setSearchText}
      isLoadingSearch={isLoadingSearch}
      onLoadMore={deploymentsQuery.hasNextPage ? deploymentsQuery.fetchNextPage : undefined}
      isLoadingMore={deploymentsQuery.isFetchingNextPage}
    />
  );
};

export const AdminDeployments = () => (
  <NavigationWrapperNoMagic requiredMLOpsGlobalPermissions={[MLOpsUserActionType.MLOPS_DEPLOYMENTS_LIST_ALL]}>
    <AdminDeploymentsConnected />
  </NavigationWrapperNoMagic>
);
